import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateWorkspaceBodySchema,
  TenantVerticalSchema,
  type TenantVertical,
} from '../../../src/shared/tenants';
import type { MeResponse } from '../../../src/shared/auth';
import { authKeys } from '../../api/auth';
import { createWorkspace } from '../../api/tenants';
import { useMe } from '../auth/useMe';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { slugifyWorkspaceName } from './slugify';

/**
 * `/onboarding` — create a workspace (ARCHITECTURE_PLAN §10; `POST /api/tenants`,
 * creator becomes owner). Form fields are name, vertical, and timezone; the
 * slug is derived from the name live (validated against the shared
 * `TenantSlugSchema` via `CreateWorkspaceBodySchema` before submit).
 *
 * On success the new owner membership is written straight into the session
 * cache (the §9 contract guarantees the creator becomes owner) so the
 * workspace shell renders immediately without a forbidden flash.
 *
 * Session-required: the route is wrapped in `RequireAuth` (§10).
 */

const VERTICAL_LABELS: Record<TenantVertical, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  roofing: 'Roofing',
};

/** US launch timezones (Assumption A8). The schema accepts any valid IANA name. */
const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

function defaultTimezone(): string {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (US_TIMEZONES as readonly string[]).includes(browserTz) ? browserTz : 'America/New_York';
}

export function OnboardingPage(): React.JSX.Element {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<TenantVertical>('hvac');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createWorkspace,
    onSuccess: ({ tenant }) => {
      // §9: the creator becomes owner. Seed the bootstrap cache so the shell's
      // membership check passes on the freshly created workspace at once.
      queryClient.setQueryData<MeResponse>(authKeys.me, (old) =>
        old === undefined
          ? old
          : {
              ...old,
              memberships: [
                ...old.memberships,
                { tenantId: tenant.id, slug: tenant.slug, name: tenant.name, role: 'owner' },
              ],
            }
      );
      void navigate(`/w/${tenant.slug}`, { replace: true });
    },
  });

  // RequireAuth guarantees a resolved, signed-in bootstrap before this page
  // renders; the pending state is only a cache-visibility edge.
  if (me.isPending || me.data === undefined) {
    return <LoadingScreen />;
  }

  const slug = slugifyWorkspaceName(name);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = CreateWorkspaceBodySchema.safeParse({ name, slug, vertical, timezone });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Check the form and try again.');
      return;
    }
    setFieldError(null);
    createMutation.mutate(parsed.data);
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-semibold tracking-tight">Create your workspace</h1>
        <p className="mt-2 text-slate-600">
          One workspace per business. You can invite your team after setup.
        </p>

        {me.data.memberships.length > 0 && (
          <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-medium text-slate-700">Your existing workspaces</h2>
            <ul className="mt-2 space-y-1">
              {me.data.memberships.map((membership) => (
                <li key={membership.tenantId}>
                  <Link
                    to={`/w/${membership.slug}`}
                    className="text-sm font-medium text-slate-900 underline underline-offset-4"
                  >
                    {membership.name}
                  </Link>
                  <span className="ml-2 text-xs text-slate-500">({membership.role})</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="workspace-name" className="block text-sm font-medium text-slate-700">
                Business name
              </label>
              <input
                id="workspace-name"
                name="name"
                type="text"
                autoComplete="organization"
                autoFocus
                required
                maxLength={120}
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
              {slug !== '' && (
                <p className="mt-1 text-sm text-slate-500">
                  Workspace URL: <code className="font-mono text-xs">/w/{slug}</code>
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="workspace-vertical"
                className="block text-sm font-medium text-slate-700"
              >
                Trade
              </label>
              <select
                id="workspace-vertical"
                name="vertical"
                value={vertical}
                onChange={(event) => setVertical(TenantVerticalSchema.parse(event.target.value))}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              >
                {TenantVerticalSchema.options.map((option) => (
                  <option key={option} value={option}>
                    {VERTICAL_LABELS[option]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="workspace-timezone"
                className="block text-sm font-medium text-slate-700"
              >
                Timezone
              </label>
              <select
                id="workspace-timezone"
                name="timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              >
                {US_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}

            {createMutation.isError && <ErrorNotice error={createMutation.error} />}

            <button
              type="submit"
              disabled={createMutation.isPending}
              className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating workspace…' : 'Create workspace'}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
